import React, { useCallback, useEffect, useMemo } from 'react';
import { useBreakpoint } from 'hooks';
import { ElementsContext as NewElementsContext } from '@bb/elements-web-rebranding';
import { ElementsContext as OldElementsContext } from '@brokerbay/elements-web';
import { featureEnabled } from 'utils';

import { useLocation } from 'react-router-dom';
import MobileClientHeight from './components/MobileClientHeight';
import { BUNDLE_OUT_OF_DATE } from '../api/interceptors';

export interface TopElementHeights {
  bbAssume: number;
  answeringAssume: number;
  globalBanner: number;
  featureBanner: number;
}

export type LayoutContextType = {
  appTitle: React.ReactNode;
  setAppTitle: React.Dispatch<React.SetStateAction<React.ReactNode>>;
  /** Total offset of all global banners (e.g. bb assume bar). Kept up to date using updateTopElementHeight */
  offsetTop: number;
  topElementHeights: TopElementHeights;
  updateTopElementHeight: (key: keyof TopElementHeights, value: number) => void;
  siderCollapsed: boolean;
  setSiderCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  getSiderCurrentWidth: () => number;
  transition: number;
  siderBaseWidth: number;
  siderCollapsedWidth: number;
  navHeight: number;
  footerHeight: number;
  scrollContainer: HTMLElement;
  setScrollContainer: React.Dispatch<React.SetStateAction<HTMLElement>>;
  isMobileView: boolean;
  chatVisibility: boolean;
  setChatVisibility: React.Dispatch<React.SetStateAction<boolean>>;
  appHeaderVisibility: boolean;
  setAppHeaderVisibility: React.Dispatch<React.SetStateAction<boolean>>;
  footerVisibility: boolean;
  // on mobile, clientHeight = window.innerHeight + address bar, use this value if you face related issues.
  clientHeight: number;
  blurBackgroundVisibility: boolean;
  setBlurBackgroundVisibility: React.Dispatch<React.SetStateAction<boolean>>;
  stopOverflow: () => void;
  startOverflow: () => void;
};

const siderBaseWidth = 220;
const siderCollapsedWidth = 80;

const LayoutContext = React.createContext<LayoutContextType>(null!);

export const LayoutContextProvider: React.FC<{
  entryRoute: 'internal' | 'external' | 'onboarding' | 'approval-subbmitted';
}> = ({ entryRoute, children }) => {
  const [blurBackgroundVisibility, setBlurBackgroundVisibility] =
    React.useState(false);
  const [appTitle, setAppTitle] = React.useState('');
  const [siderCollapsed, setSiderCollapsed] = React.useState(false);
  const [chatVisibility, setChatVisibility] = React.useState(true);
  const [appHeaderVisibility, setAppHeaderVisibility] = React.useState(true);

  const [scrollContainer, setScrollContainer] = React.useState<HTMLElement>(
    null!,
  );
  const [clientHeight, setClientHeight] = React.useState<number>(0);

  const [topElementHeights, setTopElementHeights] =
    React.useState<TopElementHeights>({
      bbAssume: 0,
      answeringAssume: 0,
      globalBanner: 0,
      featureBanner: 0,
    });

  const hasSupraRebrandingFeature = useMemo(
    () => featureEnabled('supraRebrandingFeature', 1),
    [],
  );

  const ElementsContext = useMemo(
    () => (hasSupraRebrandingFeature ? NewElementsContext : OldElementsContext),
    [hasSupraRebrandingFeature],
  );

  const clientRef = useCallback(node => {
    if (node !== null) {
      // NOTE: may be different value than window.innerHeight on mobile devices
      setClientHeight(node.getBoundingClientRect().height);
    }
  }, []);

  const offsetTop = Object.values(topElementHeights).reduce(
    (acc, cur) => acc + cur,
    0,
  );

  const updateTopElementHeight = React.useCallback(
    (key: keyof TopElementHeights, value: number) => {
      setTopElementHeights(prev => {
        if (prev[key] === value) return prev;
        return { ...prev, [key]: value };
      });
    },
    [setTopElementHeights],
  );

  const { md } = useBreakpoint();

  const getSiderCurrentWidth = () => {
    if (siderCollapsed) {
      if (!md) return 0;
      return siderCollapsedWidth;
    }
    return siderBaseWidth;
  };

  const footerVisibility = useMemo(
    () => entryRoute === 'external' && md,
    [entryRoute, md],
  );

  const footerHeight = useMemo(
    () => (footerVisibility ? 48 : 0),
    [footerVisibility],
  );

  const location = useLocation();

  useEffect(() => {
    if (
      window?.location &&
      window.sessionStorage.getItem(BUNDLE_OUT_OF_DATE) === 'true'
    ) {
      window.sessionStorage.removeItem(BUNDLE_OUT_OF_DATE);
      window.location.reload();
    }
  }, [location.pathname]);

  const stopOverflow = () => {
    document.body.style.overflow = 'hidden';
  };

  const startOverflow = () => {
    document.body.style.overflow = '';
  };

  return (
    <LayoutContext.Provider
      value={{
        appTitle,
        setAppTitle,
        offsetTop,
        updateTopElementHeight,
        topElementHeights,
        siderCollapsed,
        setSiderCollapsed,
        transition: 0.2,
        siderBaseWidth,
        siderCollapsedWidth,
        navHeight: appHeaderVisibility ? 64 : 0,
        footerHeight,
        getSiderCurrentWidth,
        scrollContainer,
        setScrollContainer,
        isMobileView: !md,
        chatVisibility,
        setChatVisibility,
        appHeaderVisibility,
        setAppHeaderVisibility,
        footerVisibility,
        clientHeight,
        blurBackgroundVisibility,
        setBlurBackgroundVisibility,
        stopOverflow,
        startOverflow,
      }}
    >
      <ElementsContext>
        <MobileClientHeight ref={clientRef} />
        {children}
      </ElementsContext>
    </LayoutContext.Provider>
  );
};

export default LayoutContext;
